import { useQuery } from '@apollo/react-hooks'
import { WatchQueryFetchPolicy } from 'apollo-client'

import { Beneficiary, Currency, UsStateCodes } from 'generated/types'

import { getBeneficiaryByLocation } from '../beneficiaryUtils'
import allEarlyBeneficiaries from '../queries/allEarlyBeneficiaries.gql'
import recurringSettings from '../queries/recurringSettings.gql'
import userTier from '../queries/userQuery.gql'
import {
  AllBeneficiaryAccounts,
  AllRecurring,
  RecurringSettings,
} from '../types'

const beneficiaryInitialState = {
  createdAt: '',
  updatedAt: '',
  lastName: '',
  id: '',
  custodianId: '',
  eligibleForContributionTooltip: false,
  firstName: '',
  ssnPresent: false,
  investmentAccount: {
    id: '',
    currentBalance: {
      value: 0,
      currency: Currency.Usd,
    },
    pendingBalance: {
      value: 0,
      currency: Currency.Usd,
    },
    recurringInvestmentSettings: {
      amount: 0,
      day: 0,
      frequency: null,
      createdAt: null,
      updatedAt: null,
    },
    transferability: {
      transferAge: 18,
      transferDate: null,
      transferStateCode: UsStateCodes.Aa,
    },
    yearToDateReturn: {
      marketValue: {
        value: 0,
        currency: Currency.Usd,
      },
      percent: 0,
    },
  },
}

const recurringInitialState = {
  id: '',
  recurringInvestmentSettings: {
    amount: 0,
    frequency: 'weekly',
    day: 0,
  },
}

export function useGetEarlyAccounts(
  routeParameters: { [key: string]: any },
  fetchPolicy?: WatchQueryFetchPolicy,
) {
  const {
    loading: isLoadingBeneficiaryAccounts,
    data: beneficiaryData,
    refetch,
  } = useQuery<AllBeneficiaryAccounts>(allEarlyBeneficiaries, {
    fetchPolicy: fetchPolicy ?? 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const { loading: isLoadingTier, data: userTierData } = useQuery(userTier, {
    fetchPolicy: fetchPolicy ?? 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { loading: isLoadingRecurringSettings, data: recurringData } =
    useQuery<AllRecurring>(recurringSettings, {
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
    })

  const allBeneficiaries = beneficiaryData?.beneficiaries ?? []
  const allRecurring = recurringData?.investmentAccountsByUserId ?? []

  const relevantBeneficiary =
    getBeneficiaryByLocation(allBeneficiaries, routeParameters) ??
    beneficiaryInitialState

  const recurringForBeneficiary =
    getBeneficiaryByLocation(
      allRecurring.map((recurringItem) => {
        return {
          ...recurringItem,
          id: recurringItem.beneficiaryId,
        }
      }),
      routeParameters,
    ) ?? recurringInitialState

  const relevantRecurring = recurringForBeneficiary?.recurringInvestmentSettings

  let isAgeOfTransferReached = false
  const transferDate =
    relevantBeneficiary?.investmentAccount?.transferability?.transferDate
  if (transferDate) {
    const currentDate = new Date()
    const ageOfTransferDate = new Date(transferDate)
    isAgeOfTransferReached = currentDate >= ageOfTransferDate
  }
  return {
    isLoadingBeneficiaryAccounts,
    isLoadingRecurringSettings,
    isLoadingTier,
    earlyAccount: relevantBeneficiary as Beneficiary,
    recurringSettings: relevantRecurring as RecurringSettings,
    invalidRoute: relevantBeneficiary.id === '',
    subscriptionTier: userTierData?.tierSubscriptionForUser?.tier?.name,
    isAgeOfTransferReached,
    allEarlyAccounts: allBeneficiaries,
    refetch,
  }
}
